import React, { MouseEventHandler, useEffect, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Route, Destination, Organization } from "../common/ObjectTypes";
import DestinationDetailsModalTable from "./DestinationDetailsModalTable";
import DestinationDetailsModalConfig from "./DestinationDetailsModalConfig";
import { PendingEdits } from "../common/configPanel/UnsavedSectionPrompt";
import { buttonClasses, routeDetailsClasses, skeletonClasses } from "../common/tailwindClassnames";
import TechnologyIcon from "../common/TechnologyIcon";
import { useHelp } from "../../context/HelpContext";

type DestinationDetailsProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    destinationTitle: string,
    destSubPage: string | undefined,
    onSelectSubPage: Function,
    // Handed down to the configuration panel so the tabs above can ask about its
    // unsaved edits before switching.
    pendingEditsRef?: React.MutableRefObject<PendingEdits | null>,
    routes: Route[],
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onToggleModal: MouseEventHandler,
    requestSort: Function,
    loadingRoutes: boolean,
    onChangeSelectedDestination: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    onChangeSelectedDestinationOwner: Function,
    // Bidirectional records: every Route this record is the source of (empty
    // when it is only ever a destination), and whether its technology could be one.
    providerRoutes?: any[],
    // The role lookups answered but failed: "none" is unknown, so the sections
    // say so rather than inviting a duplicate Route.
    providerRoleFailed?: boolean,
    destRoutesFailed?: boolean,
    canBeDataSource?: boolean,
    canBeDestination?: boolean,
    providerRoleLoaded?: boolean,
    onUseAsDataSource?: () => void,
    onAddToRoute?: () => void,
    onSave: (e: React.FormEvent<Element>) => void,
    onReloadDestination: () => void,
    // Resolves once the reloaded record is in state, so the configuration editor
    // can hold its saved values on screen until the fetched ones match.
    onReloadDestinationAndWait?: () => Promise<void>,
    changesMade: boolean,
    errors: any,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    scrollableContentRef?: RefObject<HTMLDivElement>,
    authToken?: string,
}

const DestinationDetailsModal = ({
    currentMemberIsAdmin,
    destination,
    destinationTitle,
    destSubPage,
    onSelectSubPage,
    pendingEditsRef,
    routes,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onToggleModal,
    requestSort,
    loadingRoutes,
    onChangeSelectedDestination,
    onTestAction,
    testActionLoading,
    testActionResponse,
    onChangeSelectedDestinationOwner,
    providerRoutes = [],
    providerRoleFailed,
    destRoutesFailed,
    canBeDataSource,
    canBeDestination,
    providerRoleLoaded,
    onUseAsDataSource,
    onAddToRoute,
    onSave,
    onReloadDestination,
    onReloadDestinationAndWait,
    changesMade,
    errors,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    scrollableContentRef,
    authToken,
}: DestinationDetailsProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    // Only allow 'details' or 'configuration' as valid subpages
    const allowedSubPages = ['details', 'configuration'];
    const subPage = allowedSubPages.includes(destSubPage || '') ? destSubPage : 'details';

    // Help cards for the details tab only: the configuration tab's cards are owned
    // by DestinationDetailsModalConfig, which sets them on mount and clears them on
    // unmount — setting anything here for that tab would race its cleanup.
    useEffect(() => {
        if (subPage !== 'details') return;
        setHelpContent({
            cards: [{
                title: t('Add a Destination to a Route'),
                description: t('Learn the three ways to add a destination to a Route.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/routes-gundi/add-destination-to-route' },
            }],
        });
    }, [subPage, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    if (!pageLoaded) return (
        <div className="z-20 flex flex-col w-full h-screen bg-white transition-colors duration-300" role="status" aria-busy="true">
            <span className="sr-only">{t("Loading…")}</span>
            {/* Header skeleton */}
            <div className={routeDetailsClasses.header}>
                <div className="max-w-list-table mx-auto w-full">
                    <div className="flex flex-col pb-2 w-full">
                        <div className={`h-8 w-56 ${skeletonClasses.base} mb-1`} />
                        <div className="flex flex-row gap-4">
                            <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                            <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                        </div>
                    </div>
                    <div className="flex flex-col my-1 w-full">
                        {/* Tabs skeleton — Configuration, Routes */}
                        <div className="flex flex-row gap-6 pt-1">
                            {["w-28", "w-24"].map((w, i) => (
                                <div key={i} className={`${w} h-4 ${skeletonClasses.base} mb-3`} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {/* Content skeleton — varies by tab */}
            <div className="bg-teal-t10 flex flex-col flex-grow min-w-[400px] -mx-8 desktop:-mx-[60px] px-8 desktop:px-[60px]">
                <div className="py-3 h-full max-w-list-table mx-auto w-full">
                  {/* A white card, like the panel each tab actually renders. */}
                  <div className="mt-5 border border-teal-t30 rounded-lg bg-white px-5 py-4">
                    {subPage === "details" ? (
                        /* Routes tab: table of Routes */
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-unlisted-neutral-gray-100">
                                    {["w-32", "w-40", "w-28"].map((w, i) => (
                                        <th key={i} className="pb-3 pr-6 text-left">
                                            <div className={`h-3 ${w} ${skeletonClasses.base}`} />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={i} className="border-b border-unlisted-neutral-gray-100">
                                        {["w-36", "w-44", "w-32"].map((w, j) => (
                                            <td key={j} className="py-3 pr-6">
                                                <div className={`h-4 ${w} ${skeletonClasses.base}`} />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        /* Configuration tab: Name + Workspace + URL fields + one action section */
                        <div className="flex flex-col gap-5 w-full">
                            {["w-16", "w-24", "w-8"].map((labelW, i) => (
                                <div key={i} className="flex flex-col gap-2">
                                    <div className={`h-4 ${labelW} ${skeletonClasses.base}`} />
                                    <div className={`h-9 w-full ${skeletonClasses.base}`} />
                                </div>
                            ))}
                            <div className="pt-8 flex flex-col gap-5">
                                <div className={`h-6 w-40 ${skeletonClasses.base}`} />
                                {["w-28", "w-32"].map((labelW, i) => (
                                    <div key={i} className="flex flex-col gap-2">
                                        <div className={`h-4 ${labelW} ${skeletonClasses.base}`} />
                                        <div className={`h-9 w-full ${skeletonClasses.base}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                  </div>
                </div>
            </div>
        </div>
    );

    return (
        <div
            key={subPage}
            /* A FIXED height, not min-height: the content pane below scrolls
               internally (scrollableContentRef), which needs this column to be
               bounded. h-screen overflowed by the shell's top offset, pushing
               the tint short of the bottom and leaving a white strip; this is
               the viewport less that offset, with -mb-6 swallowing the shell's
               bottom padding. */
            className="z-20 flex flex-col w-full h-[calc(100vh-104px)] md:h-[calc(100vh-94px)] -mb-6 bg-white transition-colors duration-300"
        >
            {/* top portion */}
            <div className={routeDetailsClasses.header}>
                <div className="max-w-list-table mx-auto w-full">
                {/* The technology's logo stands beside the two text lines it
                    describes (name + pills), so it is sized to their combined height. */}
                <div className="flex flex-row items-start gap-3 pb-2 w-full bg-white transition-colors duration-300">
                    <TechnologyIcon
                        typeValue={destination?.type?.value}
                        typeName={destination?.type?.name}
                        boxClassName="h-12 w-12 text-2xl mt-1"
                        imgClassName="h-12 w-12 object-contain" />
                    <div className="flex flex-col min-w-0">
                        <h1 className="text-2xl sm:text-3xl font-semibold text-ui-text-primary-text transition-colors duration-300">{destinationTitle}</h1>
                        {/* Same header pills as the Route page: technology first, no labels. */}
                        <div className="flex flex-row items-center gap-2 mt-2 mb-3">
                            {destination?.type?.name && <span className={routeDetailsClasses.headerPill}>{destination.type.name}</span>}
                            {destination?.owner?.name && <span className={routeDetailsClasses.headerPill}>{destination.owner.name}</span>}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col my-1 w-full bg-white transition-colors duration-300">
                    {/* menu tabs */}
                    <div className="w-full flex flex-row font-semibold text-ui-text-primary-text transition-colors duration-300">
                        <label data-testid="ConnectionDetails-configuration-tab" className={(destSubPage === "configuration") ? "border-2 border-t-white border-l-white border-r-white border-b-ui-cta-primary text-ui-text-primary-text mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white mr-3 py-3 cursor-pointer transition-colors duration-300"}
                            onClick={() => onSelectSubPage("configuration")}>
                            {t("Configuration")}
                        </label>
                        <label data-testid="ConnectionDetails-details-tab" className={(destSubPage === "details") ? "border-2 border-t-white border-l-white border-r-white border-b-ui-cta-primary text-ui-text-primary-text mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white mr-3 py-3 cursor-pointer transition-colors duration-300"}
                            onClick={() => onSelectSubPage("details")}>
                            {t("Routes")}
                        </label>
                    </div>
                </div>
                </div>
            </div>

            {/* scrollable content */}
            <div
                key={destSubPage}
                /* No w-full: an explicit width pins the right edge, so the negative
                   right margin cannot reach the viewport and the shell's gutter
                   shows as a pale strip down that side. The negative margins
                   size the box on their own. */
                className="bg-teal-t10 -mx-8 desktop:-mx-[60px] px-8 desktop:px-[60px] flex flex-col overflow-y-auto flex-grow min-w-[400px] scrollbar-thin scrollbar-thumb-unlisted-neutral-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-unlisted-neutral-gray-500"
                ref={scrollableContentRef}
            >
                <div className="py-3 h-full max-w-list-table mx-auto w-full transition-colors duration-300">
                        {/* One white panel around the whole tab — both tables and
                            their buttons — so it reads as a card on the tint like
                            the Configuration tab's panels do. */}
                        {(destSubPage === "details") && <div className="mt-8 border border-teal-t30 rounded-lg bg-white px-5 py-4">
                            {/* A record plays a role per Route. Each role shows the
                                routes it appears in as a table, or — when it plays
                                that role nowhere — a line saying so plus the way in. */}
                            {/* Section actions sit on the title line, right-aligned. */}
                            <div className="flex flex-row items-center justify-between mb-2">
                                <h2 className="text-lg font-semibold text-ui-text-primary-text">{t("Sending data to")}</h2>
                                {/* A Connection holds one API key, so it can only be
                                    the source of a single Route — once it already
                                    provides one, the button goes away. Only shown
                                    once the source-role lookup has settled, to avoid
                                    double creation while "no route" is still unknown. */}
                                {canBeDataSource && providerRoleLoaded && !providerRoleFailed && providerRoutes.length === 0 && (
                                    <button type="button" className={buttonClasses.lightGreen}
                                        onClick={() => onUseAsDataSource?.()}>
                                        {t("Create Route")}
                                    </button>
                                )}
                            </div>
                            <div className="mb-8">
                                {!providerRoleLoaded
                                    ? <div className="flex flex-col gap-2">
                                        {[0, 1].map((i) => <div key={i} className={`h-4 w-64 ${skeletonClasses.base}`} />)}
                                      </div>
                                    : providerRoleFailed
                                    ? <p className="text-sm text-ui-text-primary-text">
                                        {t("Couldn't load the Routes for this Connection. Reload the page to try again.")}
                                      </p>
                                    : providerRoutes.length > 0
                                    ? <DestinationDetailsModalTable
                                        routes={providerRoutes}
                                        requestSort={() => {}}
                                        loadingRoutes={false}
                                        onPageClicked={() => {}}
                                        totalCount={providerRoutes.length}
                                        pageLoaded={pageLoaded}
                                        take={take}
                                        offset={0}
                                        showPagination={false}
                                        sortable={false}
                                        showDestinationTech={true}
                                      />
                                    : <p className="text-sm text-ui-text-primary-text">
                                        {canBeDataSource
                                            ? t("Not used as a source yet.")
                                            : t("This technology cannot send data to Gundi, so it is only ever a destination.")}
                                      </p>}
                            </div>
                            <div className="flex flex-row items-center justify-between mb-2">
                                <h2 className="text-lg font-semibold text-ui-text-primary-text">{t("Receiving data from")}</h2>
                                {/* A record can be a destination in any number of
                                    routes, so this stays available once it is
                                    already in one. */}
                                {canBeDestination && (
                                    <button type="button" className={buttonClasses.lightGreen}
                                        onClick={() => onAddToRoute?.()}>
                                        {t("Include in Route")}
                                    </button>
                                )}
                            </div>
                            {!loadingRoutes && routes.length === 0 && (
                                <p className="text-sm text-ui-text-primary-text">
                                    {destRoutesFailed
                                        ? t("Couldn't load the Routes for this Connection. Reload the page to try again.")
                                        : canBeDestination
                                            ? t("Not used as a destination yet.")
                                            : t("This technology cannot receive data from Gundi, so it is only ever a source.")}
                                </p>
                            )}
                            {(loadingRoutes || routes.length > 0) && <DestinationDetailsModalTable
                                routes={routes}
                                requestSort={requestSort}
                                loadingRoutes={loadingRoutes}
                                onPageClicked={onPageClicked}
                                totalCount={totalCount}
                                pageLoaded={pageLoaded}
                                take={take}
                                offset={offset}
                            />}
                        </div>}
                        {(destSubPage === "configuration") && <DestinationDetailsModalConfig
                            currentMemberIsAdmin={currentMemberIsAdmin}
                            destination={destination}
                            organizations={organizations}
                            loadMoreOrgs={loadMoreOrgs}
                            orgInputChange={orgInputChange}
                            onChange={onChangeSelectedDestination}
                            onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
                            onTestAction={onTestAction}
                            testActionLoading={testActionLoading}
                            testActionResponse={testActionResponse}
                            onSave={onSave}
                            onReloadDestination={onReloadDestination}
                            onReloadDestinationAndWait={onReloadDestinationAndWait}
                            changesMade={changesMade}
                            onToggleModal={onToggleModal}
                            errors={errors}
                            authToken={authToken}
                            pendingEditsRef={pendingEditsRef}
                        />}
                    </div>
            </div>
        </div>
    );
};

export default DestinationDetailsModal;